import { z } from "zod";
import { type Project, type Values, adjustmentSchema } from "./schema";
import { damSelectionSchema } from "./dam";
import { statementSelectionSchema } from "./statements";
import { effectiveFields, validateSubmission, type Context } from "./engine";
import { isDynamicChoiceField } from "./dynamic-choices";
export const bulkSourceSchema = z
  .object({
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    bytes: z.number().int().min(1).max(10000000),
    format: z.enum(["csv", "tsv", "delimited", "xlsx"]),
    encoding: z.enum(["utf-8", "utf-16le", "utf-16be", "windows-1252", "xlsx"]),
    delimiter: z.string().max(1),
    headerRow: z.number().int().min(1).max(1048576),
    worksheetIndex: z.number().int().min(0).max(19),
    columns: z.number().int().min(1).max(500),
    headers: z.array(z.string().min(1).max(32768)).min(1).max(500),
    rowCount: z.number().int().min(1).max(10000),
    lastRow: z.number().int().min(1).max(1048576).optional(),
    originalHeaders: z.array(z.string().max(32768)).max(500).optional(),
  })
  .strict()
  .superRefine((s, ctx) => {
    if (s.originalHeaders && s.originalHeaders.length !== s.columns)
      ctx.addIssue({
        code: "custom",
        message: "Original headers must match the source column count",
      });
    const last = s.lastRow ?? s.headerRow + s.rowCount;
    if (last > 1048576 || last - s.headerRow < s.rowCount)
      ctx.addIssue({
        code: "custom",
        message: "Source row range is inconsistent",
      });
    if (
      (s.format === "xlsx") !== (s.encoding === "xlsx") ||
      (s.format === "xlsx" && s.delimiter !== "") ||
      (s.format !== "xlsx" && s.delimiter.length !== 1)
    )
      ctx.addIssue({
        code: "custom",
        message: "Source format, encoding and delimiter must agree",
      });
  });
export const bulkMappingSchema = z
  .object({
    version: z.literal(1),
    confirmed: z.literal(true),
    headerRow: z.number().int().min(1).max(1048576),
    worksheetIndex: z.number().int().min(0).max(19),
    columns: z
      .array(
        z
          .object({
            column: z.number().int().min(0).max(499),
            fieldId: z.string().min(1).max(200).optional(),
            header: z.string().min(1).max(32768),
            originalHeader: z.string().max(32768).optional(),
            disposition: z.enum(["map", "ignore"]),
            confirmed: z.literal(true),
          })
          .strict(),
      )
      .min(1)
      .max(500),
  })
  .strict();
const scalar = z.union([
  statementSelectionSchema,
  damSelectionSchema.extend({ adjustments: adjustmentSchema.optional() }),
  z
    .object({
      assetId: z.string().regex(/^asset_[\w-]+$/),
      adjustments: adjustmentSchema.optional(),
    })
    .strict(),
  z.string().max(32768),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string().max(32768)).max(10000),
]);
export const bulkRequestSchema = z
  .object({
    version: z.literal(1),
    source: bulkSourceSchema,
    mapping: bulkMappingSchema,
    rows: z
      .array(
        z
          .object({
            sourceRow: z.number().int().positive(),
            values: z.record(z.string(), scalar),
          })
          .strict(),
      )
      .min(1)
      .max(10000),
  })
  .strict();
export type BulkRequest = z.infer<typeof bulkRequestSchema>;
export type BulkSource = z.infer<typeof bulkSourceSchema>;
export const bulkFieldEligible = (
  field: ReturnType<typeof effectiveFields>[number],
) =>
  field.editable &&
  !field.system &&
  !field.autoGenerated &&
  !field.calculation &&
  field.source.kind !== "lookup" &&
  (field.source.kind === "dam" ||
    !["address", "preset", "calendar", "image-list", "map"].includes(
      field.type,
    ));
/** Header-only CSV: users supply one row per document; no profile/default data is exported. */
export function createBulkTemplate(project: Project, partId?: string) {
  if (partId && !project.parts.some((part) => part.id === partId))
    throw new Error("Unknown template part");
  const fields = effectiveFields(project, partId).filter(bulkFieldEligible);
  const identifiers = fields.map(
    (field) => `field:${encodeURIComponent(field.id)}`,
  );
  const columns = fields.map((field, index) => ({
    fieldId: field.id,
    // Qualified IDs keep blank/duplicate/unsafe keys unambiguous and spreadsheet-safe.
    header:
      field.key &&
      field.key === field.key.trim() &&
      !/^[=+\-@]/.test(field.key) &&
      !/[\u0000-\u001f\u007f]/.test(field.key) &&
      fields.filter((other) => other.key === field.key).length === 1 &&
      !identifiers.includes(field.key)
        ? field.key
        : identifiers[index],
    key: field.key,
    label: field.label,
    type: field.type,
    required: field.required,
    conditional: field.hidden || !!field.visibleWhen,
    choices: field.choices.map((choice) => String(choice.value)),
    help:
      field.source.kind === "dam"
        ? `${field.help} Paste JSON from Download image selection for this document, variable and part.`.trim()
        : field.source.kind === "statement"
          ? `${field.help} Enter custom text or paste JSON from Download statement selection for this group version.`.trim()
          : field.type === "image"
            ? `${field.help} Enter a direct HTTP or HTTPS image URL, or a managed asset ID.`.trim()
            : field.help,
  }));
  if (columns.length > 500)
    throw new Error("Bulk templates support up to 500 columns");
  return {
    columns,
    csv: columns.length
      ? columns
          .map((column) => `"${column.header.replaceAll('"', '""')}"`)
          .join(",") + "\r\n"
      : "",
  };
}
export function convertBulkCell(
  field: ReturnType<typeof effectiveFields>[number],
  value: Values[string],
): Values[string] {
  if (
    field.source.kind === "statement" &&
    typeof value === "string" &&
    value.trim().startsWith("{")
  ) {
    try {
      const parsed = JSON.parse(value);
      if (
        parsed &&
        typeof parsed === "object" &&
        Object.hasOwn(parsed, "statement")
      )
        return parsed;
    } catch {
      /* Custom text remains literal. */
    }
  }
  if (
    field.source.kind === "dam" &&
    typeof value === "string" &&
    value.trim().startsWith("{")
  ) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  if (field.type !== "checkbox" || typeof value !== "string") return value;
  if (field.choices.length || isDynamicChoiceField(field)) {
    if (value.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) &&
          parsed.every((item) => typeof item === "string")
          ? parsed
          : value;
      } catch {
        return value;
      }
    }
    return value ? value.split(";") : [];
  }
  const token = value.trim().toLowerCase();
  if (["true", "yes", "1", "on", "y"].includes(token)) return true;
  if (["false", "no", "0", "off", "n"].includes(token)) return false;
  return value;
}
export function validateBulkMapping(
  project: Project,
  request: BulkRequest,
  partId?: string,
  context?: Context,
) {
  const { source, mapping } = request;
  if (
    source.headerRow !== mapping.headerRow ||
    source.worksheetIndex !== mapping.worksheetIndex
  )
    throw new Error("Mapping coordinates do not match the source");
  const columns = new Set<number>(),
    targets = new Set<string>(),
    fields = effectiveFields(project, partId, context);
  if (
    mapping.columns.length !== source.columns ||
    source.headers.length !== source.columns
  )
    throw new Error("Every source column must be explicitly mapped or ignored");
  for (const entry of mapping.columns) {
    if (
      entry.column >= source.columns ||
      columns.has(entry.column) ||
      (entry.fieldId && targets.has(entry.fieldId))
    )
      throw new Error("Mapping columns and targets must be valid and unique");
    if (
      entry.header !== source.headers[entry.column] ||
      (entry.originalHeader !== undefined &&
        entry.originalHeader !==
          (source.originalHeaders?.[entry.column] ??
            source.headers[entry.column]))
    )
      throw new Error("Source column header changed");
    columns.add(entry.column);
    if (entry.disposition === "ignore") {
      if (entry.fieldId) throw new Error("Ignored columns cannot have targets");
      continue;
    }
    const field = fields.find((f) => f.id === entry.fieldId);
    if (!field || !bulkFieldEligible(field))
      throw new Error("Mapping target is incompatible or not editable");
    columns.add(entry.column);
    targets.add(entry.fieldId!);
  }
  const missing = validateSubmission(
    project,
    {},
    partId,
    context,
  ).errors.filter(
    (e) =>
      e.code === "REQUIRED" &&
      fields.some((f) => f.id === e.fieldId && bulkFieldEligible(f)) &&
      !targets.has(e.fieldId!),
  );
  if (missing.length)
    throw new Error(
      "Required scalar targets need a mapping or a valid default",
    );
  const rows = new Set<number>();
  for (const row of request.rows) {
    if (
      row.sourceRow <= source.headerRow ||
      row.sourceRow > (source.lastRow ?? source.headerRow + source.rowCount) ||
      rows.has(row.sourceRow)
    )
      throw new Error("Invalid or duplicate source row");
    rows.add(row.sourceRow);
    if (Object.keys(row.values).some((id) => !targets.has(id)))
      throw new Error("Row contains an unmapped or injected variable");
  }
}
export function validateBulkRows(
  project: Project,
  request: BulkRequest,
  partId?: string,
  context?: Context,
) {
  validateBulkMapping(project, request, partId, context);
  const fields = effectiveFields(project, partId, context);
  return request.rows.map((input) => {
    const row = {
      ...input,
      values: Object.fromEntries(
        Object.entries(input.values).map(([id, value]) => [
          id,
          convertBulkCell(
            fields.find((f) => f.id === id)!,
            value,
          ),
        ]),
      ),
    };
    const errors = Object.entries(row.values)
      .filter(
        ([id, value]) =>
          Array.isArray(value) &&
          !(
            fields.find((f) => f.id === id)?.type === "checkbox" &&
            (fields.find((f) => f.id === id)!.choices.length ||
              isDynamicChoiceField(fields.find((f) => f.id === id)!))
          ),
      )
      .map(([id]) => ({
        fieldId: id,
        code: "SCALAR",
        message: "A scalar value is required for this mapped target",
      }));
    const validation = validateSubmission(
      project,
      row.values as Values,
      partId,
      context,
    );
    return {
      sourceRow: row.sourceRow,
      values: validation.snapshot,
      diagnostics: [...validation.errors, ...errors].map((error) => ({
        fieldId: error.fieldId,
        code: error.code,
        message: error.message,
      })),
    };
  });
}
